import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CreateAdminQuoteNewCustomer,
  CreateAdminQuoteResult,
  Customer,
  DealCategory,
} from "@/core";
import { ProductType } from "@/core/entities/product";
import { useCreateAdminQuote } from "@/hooks";
import { useProductsStore } from "@/stores/products";
import { addDays, format } from "date-fns";
import { Check, Copy } from "lucide-react";
import { useMemo, useState } from "react";
import { QuoteContactFields } from "./QuoteContactFields";
import { QuotePriceField } from "./QuotePriceField";

const DEFAULT_VALID_DAYS = 7;
const MIN_VALID_DAYS = 1;
const MAX_VALID_DAYS = 30;
const NO_STATUS_TIER = "none";
const EMPTY_CONTACT: CreateAdminQuoteNewCustomer = {
  firstName: "",
  lastName: "",
  email: "",
  countryCode: "1",
  phone: "",
};

type QuoteSide = "buy" | "sell";

export interface CreateQuoteDefaults {
  productId?: string;
  buyOrSell?: QuoteSide;
  quantity?: number;
  statusTier?: string;
}

interface CreateQuoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Leave out to type the customer's details in by hand. */
  customer?: Pick<Customer, "id" | "firstName" | "lastName" | "email">;
  /** Prefills the form, e.g. from the deal being re-quoted. */
  defaults?: CreateQuoteDefaults;
}

/**
 * Creates a quote and hands back its offer link, so staff can quote without
 * the customer filling in the website form. The customer is either the one
 * passed in or typed in by hand, in which case they need not exist yet.
 */
export function CreateQuoteDialog({
  open,
  onOpenChange,
  customer,
  defaults,
}: Readonly<CreateQuoteDialogProps>) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Quote</DialogTitle>
          <DialogDescription>
            {customer
              ? `For ${customer.firstName} ${customer.lastName} (${customer.email}). `
              : "If the email matches an existing customer, the quote goes on their record. "}
            Priced like the website form, with a link you can send them.
          </DialogDescription>
        </DialogHeader>
        {/* Dialog content unmounts on close, so each open starts afresh. */}
        <CreateQuoteForm
          customerId={customer?.id}
          defaults={defaults}
          onDone={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function CreateQuoteForm({
  customerId,
  defaults,
  onDone,
}: Readonly<{
  customerId?: string;
  defaults?: CreateQuoteDefaults;
  onDone: () => void;
}>) {
  const { products } = useProductsStore();
  const createQuoteMutation = useCreateAdminQuote();

  const [buyOrSell, setBuyOrSell] = useState<QuoteSide>(
    defaults?.buyOrSell ?? "sell",
  );
  const [productId, setProductId] = useState(defaults?.productId ?? "");
  const [quantity, setQuantity] = useState(
    defaults?.quantity ? String(defaults.quantity) : "",
  );
  const [statusTier, setStatusTier] = useState(
    defaults?.statusTier ?? NO_STATUS_TIER,
  );
  const [promoCode, setPromoCode] = useState("");
  // Null while the price follows the calculated rate.
  const [priceInput, setPriceInput] = useState<string | null>(null);
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [validForDays, setValidForDays] = useState(String(DEFAULT_VALID_DAYS));
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateAdminQuoteResult | null>(null);

  // Only products the offer page can show for this side. Credit card points
  // sellers skip the offer page entirely, so they can't be quoted here.
  const quotableProducts = useMemo(
    () =>
      products
        .filter(
          (product) =>
            !product.inactive &&
            (product.buyOrSell === ProductType.BOTH ||
              product.buyOrSell === buyOrSell) &&
            !(
              buyOrSell === "sell" &&
              product.category === DealCategory.CreditCardPoints
            ),
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [products, buyOrSell],
  );

  const product = quotableProducts.find((item) => item.id === productId);
  const statusTiers = buyOrSell === "sell" ? (product?.statusTiers ?? []) : [];
  const limits = product
    ? buyOrSell === "sell"
      ? {
          min: product.minimumValueForSell,
          max: product.maximumValueForSell,
        }
      : { min: product.minimumValueForBuy, max: product.maximumValueForBuy }
    : null;

  const parsedQuantity = Number(quantity);
  // Priced at the capped quantity, as the server does.
  const pricedMiles =
    limits && parsedQuantity > 0
      ? Math.min(parsedQuantity, limits.max)
      : undefined;
  const sidePrices =
    buyOrSell === "sell" ? product?.sellPrices : product?.buyPrices;
  const calculatedPricePerMile =
    pricedMiles === undefined
      ? undefined
      : sidePrices?.find(
          (tier) =>
            tier.minRange <= pricedMiles && tier.maxRange >= pricedMiles,
        )?.price;
  const parsedPrice = priceInput === null ? undefined : Number(priceInput);
  const isPriceValid =
    parsedPrice === undefined ||
    (Number.isFinite(parsedPrice) && parsedPrice > 0);
  // Only sent when it differs, so retyping the calculated rate stays standard.
  const pricePerMileOverride =
    parsedPrice !== undefined && parsedPrice !== calculatedPricePerMile
      ? parsedPrice
      : undefined;
  const parsedValidForDays = Number(validForDays);
  const isValidForDaysInRange =
    Number.isInteger(parsedValidForDays) &&
    parsedValidForDays >= MIN_VALID_DAYS &&
    parsedValidForDays <= MAX_VALID_DAYS;
  const isContactComplete =
    !!customerId ||
    (Object.values(contact).every((value) => value.trim().length > 0) &&
      contact.email.includes("@"));
  const canSubmit =
    !!product &&
    isContactComplete &&
    Number.isInteger(parsedQuantity) &&
    parsedQuantity > 0 &&
    isPriceValid &&
    isValidForDaysInRange &&
    !createQuoteMutation.isPending;

  const handleSideChange = (value: string) => {
    setBuyOrSell(value as QuoteSide);
    setStatusTier(NO_STATUS_TIER);
    setPriceInput(null);
    setError(null);
  };

  const handleProductChange = (value: string) => {
    setProductId(value);
    setStatusTier(NO_STATUS_TIER);
    setPriceInput(null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!product) return;

    setError(null);
    try {
      const created = await createQuoteMutation.mutateAsync({
        // Only one key may be sent: the callable turns undefined into null.
        ...(customerId
          ? { customerId }
          : {
              customer: {
                firstName: contact.firstName.trim(),
                lastName: contact.lastName.trim(),
                email: contact.email.trim(),
                countryCode: contact.countryCode.trim(),
                phone: contact.phone.trim(),
              },
            }),
        productId: product.id,
        buyOrSell,
        quantity: parsedQuantity,
        statusTier:
          statusTier === NO_STATUS_TIER || statusTiers.length === 0
            ? undefined
            : statusTier,
        promoCode: promoCode.trim() || undefined,
        pricePerMile: pricePerMileOverride,
        validForDays: parsedValidForDays,
      });
      setResult(created);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Could not create the quote. Please try again.",
      );
    }
  };

  if (result) {
    return (
      <CreatedQuote
        result={result}
        matchedExistingCustomer={!customerId && !result.isNewCustomer}
        onDone={onDone}
      />
    );
  }

  return (
    <>
      <div className="space-y-4">
        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {!customerId && (
          <QuoteContactFields
            contact={contact}
            onChange={(changed) => {
              setContact(changed);
              setError(null);
            }}
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label>Customer wants to</Label>
            <Select value={buyOrSell} onValueChange={handleSideChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sell">Sell</SelectItem>
                <SelectItem value="buy">Buy</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Link valid for (days)</Label>
            <Input
              type="number"
              min={MIN_VALID_DAYS}
              max={MAX_VALID_DAYS}
              step={1}
              value={validForDays}
              onChange={(event) => setValidForDays(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {isValidForDaysInRange
                ? `Expires ${format(addDays(new Date(), parsedValidForDays), "MMM d, yyyy h:mm a")}`
                : `Enter ${MIN_VALID_DAYS} to ${MAX_VALID_DAYS} days.`}
            </p>
          </div>
        </div>

        <div className="space-y-1">
          <Label>Product</Label>
          <SearchableSelect
            value={product ? product.id : ""}
            onChange={handleProductChange}
            options={quotableProducts.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            placeholder="Select a product"
            searchPlaceholder="Search products..."
            emptyText="No products available for this side."
          />
        </div>

        <div className="space-y-1">
          <Label>Miles / points</Label>
          <Input
            type="number"
            min={1}
            step={1}
            value={quantity}
            onChange={(event) => {
              setQuantity(event.target.value);
              setError(null);
            }}
            placeholder="100000"
          />
          {limits && (
            <p className="text-xs text-muted-foreground">
              Between {limits.min.toLocaleString()} and{" "}
              {limits.max.toLocaleString()}.
            </p>
          )}
        </div>

        {product && (
          <QuotePriceField
            calculatedPricePerMile={calculatedPricePerMile}
            value={priceInput}
            onChange={(value) => {
              setPriceInput(value);
              setError(null);
            }}
            miles={pricedMiles}
          />
        )}

        {statusTiers.length > 0 && (
          <div className="space-y-1">
            <Label>Status tier</Label>
            <Select value={statusTier} onValueChange={setStatusTier}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_STATUS_TIER}>No status</SelectItem>
                {statusTiers.map((tier) => (
                  <SelectItem key={tier.name} value={tier.name}>
                    {tier.name} (+{tier.percentage}%)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-1">
          <Label>Promo code (optional)</Label>
          <Input
            value={promoCode}
            onChange={(event) => {
              setPromoCode(event.target.value);
              setError(null);
            }}
            placeholder="SAVE5"
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          loading={createQuoteMutation.isPending}
          loadingText="Creating..."
        >
          Create Quote
        </Button>
      </DialogFooter>
    </>
  );
}

function CreatedQuote({
  result,
  matchedExistingCustomer,
  onDone,
}: Readonly<{
  result: CreateAdminQuoteResult;
  matchedExistingCustomer: boolean;
  onDone: () => void;
}>) {
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(result.offerUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <div className="space-y-3">
        <p className="text-sm">
          Quote created. Send this link to the customer — it stays open until{" "}
          <span className="font-medium">
            {format(new Date(result.expiresAt), "MMM d, yyyy h:mm a")}
          </span>
          .
        </p>
        {matchedExistingCustomer && (
          <p className="text-sm text-muted-foreground">
            That email belongs to an existing customer, so the quote is on their
            record with the name and phone we already have.
          </p>
        )}
        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={result.offerUrl}
            className="font-mono text-xs"
            onFocus={(event) => event.target.select()}
          />
          <Button variant="outline" size="icon" onClick={copy}>
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      <DialogFooter>
        <Button onClick={onDone}>Done</Button>
      </DialogFooter>
    </>
  );
}
